import type { PropsWithChildren } from 'react';
import { Children, memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import ReactDOM from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import { CSSTransition } from 'react-transition-group';
import type { DefaultTheme } from 'styled-components';
import { ThemeContext, ThemeProvider } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Media } from '../../../../../theme/Media.tsx';
import { palette } from '../../../../Palette/index.ts';
import { ITEMS_LIST_PADDING_VALUE } from '../../constants.ts';
import { usePuzzleNavigationContext } from '../../context.tsx';
import { usePathMatch } from '../../hooks/index.ts';
import type { RibbonVariant } from '../../types.ts';
import { CornerBadge } from '../CornerBadge.tsx';
import { ANIMATION_CLICK, ANIMATION_HOVER, ANIMATION_STATE } from './constants.ts';
import type { IconType } from './HeaderWrapper.tsx';
import { HeaderWrapper } from './HeaderWrapper.tsx';
import { IconAngle, ItemElement, ItemHeader, MotionDiv, MotionDivWrapper, SubList, SubListTitle, Wrapper } from './styled.ts';

const calculateTranslateListY = (itemWrapperTop: number, itemListHeight: number) => {
  const elTransform = itemListHeight - (window.innerHeight - itemWrapperTop - 2);
  return Math.max(elTransform, 0);
};

export interface IItem extends PropsWithChildren {
  icon: IconType;
  title: string;
  label?: string;
  itemKey: string;
  path?: string | string[];
  dataTestId?: string;
  theme?: DefaultTheme;
  standalone?: boolean;
  ribbon?: RibbonVariant;
  showIndicator?: boolean;
}

const Item = ({ children, title, icon, itemKey, path, dataTestId, theme, label, standalone, ribbon, showIndicator }: IItem) => {
  const match = usePathMatch({ path });
  const isDesktop = useMediaQuery({ query: Media.lgMin });
  const { activeKey, setActiveKey, isCollapsedNav } = usePuzzleNavigationContext();

  const currentTheme = useContext(ThemeContext);
  const effectiveTheme = theme ?? currentTheme ?? palette.primary;

  const itemWrapper = useRef<HTMLDivElement>(null);
  const itemList = useRef<HTMLDivElement>(null);
  const itemsWrapper = useRef<HTMLDivElement>(null);

  const isOpened = useMemo(() => activeKey === itemKey, [activeKey, itemKey]);

  const [coords, setCoords] = useState<{ $top: number; $left: number } | undefined>(undefined);

  const handleOpenMenu = useCallbackRef(() => setActiveKey(itemKey));
  const handleCloseMenu = useCallbackRef(() => setActiveKey(null));
  const handleToggleMenu = useCallbackRef(() => setActiveKey((v) => (v === itemKey ? null : itemKey)));

  const handleAnimationUpdate = useCallbackRef(() => {
    itemsWrapper.current?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' });
  });

  useEffect(() => {
    if (isCollapsedNav) {
      setActiveKey(null);
    }
  }, [isCollapsedNav, setActiveKey]);

  useEffect(() => {
    if (activeKey) {
      const top = itemWrapper.current?.getBoundingClientRect().top ?? 0;
      const left = itemWrapper.current?.getBoundingClientRect().right ?? 0;
      const itemListHeight = itemList.current?.getBoundingClientRect().height ?? 0;

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCoords({
        $top: top - calculateTranslateListY(top, itemListHeight),
        $left: left - ITEMS_LIST_PADDING_VALUE,
      });
    }
  }, [activeKey]);

  return (
    <ThemeProvider theme={effectiveTheme}>
      <Wrapper
        ref={itemWrapper}
        onMouseEnter={isCollapsedNav ? handleOpenMenu : undefined}
        onMouseLeave={isCollapsedNav ? handleCloseMenu : undefined}
        data-testid={dataTestId}>
        <ItemElement $standalone={standalone} $isCollapsed={isCollapsedNav} $isOpened={isOpened}>
          <ItemHeader $collapsed={isCollapsedNav} onClick={!isCollapsedNav ? handleToggleMenu : undefined}>
            {ribbon && <CornerBadge variant={ribbon} />}
            <HeaderWrapper
              match={match}
              icon={icon}
              title={title}
              label={label}
              showIndicator={showIndicator}
              isCollapsedNav={isCollapsedNav}
            />
            {!isCollapsedNav && <IconAngle align={isOpened ? 'top' : 'bottom'} />}
          </ItemHeader>
        </ItemElement>
        <CSSTransition in={isOpened} timeout={150} unmountOnExit={isCollapsedNav}>
          <>
            {isCollapsedNav && isDesktop ? (
              ReactDOM.createPortal(
                <MotionDiv
                  animate={isOpened ? ANIMATION_STATE.OPEN : ANIMATION_STATE.CLOSED}
                  variants={ANIMATION_HOVER}
                  coords={coords}
                  initial={ANIMATION_STATE.CLOSED}>
                  <MotionDivWrapper ref={itemList} $isCollapsedNav>
                    <SubListTitle>{title}</SubListTitle>
                    <SubList>{Children.map(children, (child) => child !== null && child)}</SubList>
                  </MotionDivWrapper>
                </MotionDiv>,
                document.body,
              )
            ) : (
              <motion.div
                animate={isOpened ? ANIMATION_STATE.OPEN : ANIMATION_STATE.CLOSED}
                variants={ANIMATION_CLICK}
                onUpdate={handleAnimationUpdate}
                initial={ANIMATION_STATE.CLOSED}>
                <MotionDivWrapper $standalone={standalone} ref={itemsWrapper}>
                  {Children.map(children, (child) => child !== null && child)}
                </MotionDivWrapper>
              </motion.div>
            )}
          </>
        </CSSTransition>
      </Wrapper>
    </ThemeProvider>
  );
};

export default memo(Item);
